import { useQuery, UseQueryOptions } from '@tanstack/react-query'
import { IAxiosResponseWithPagination } from 'types/entities'
import {
	IDsmesBillableSessions,
	IDsmesClients,
	IDsmesConference,
	IDsmesDashboardCounts,
	IDsmesParams,
	IDsmesPatientEnrolled,
	IDsmesSessionCompleted,
	IFullProviderDsmes,
} from './Dsmes.Intefaces'
import axios, { AxiosResponse } from 'axios'
import { ENDPOINTS } from 'const/endpoints'
import {
	getDsmesBillableSessions,
	getDsmesClients,
	getDsmesSessions,
	getDsmesDashboardCounts,
	getDsmesPatientEnrolled,
	getDsmesRegisteredSessionsCompleted,
	getDsmesRetentionRate,
	getDsmesTotalCompletedSessions,
	getPartnerListProviderWise,
	getProgramListingForSuperAdmin,
} from './Dsmes.service'
import { ReactComponent as ReportIconBlue } from 'assets/images/reportFountainBlue.svg'
import { ReactComponent as ReportIconGreen } from 'assets/images/ReportGreen.svg'
import { ReactComponent as RightArrowDarkGreen } from 'assets/images/ReportDarkGreen.svg'
import { ReactComponent as RightArrowDarkBlue } from 'assets/images/Report_Dark_blue.svg'

/**
 * DSMES DASHBOARD COUNTS
 */

interface IDsmesDashboardCountQueryProps
	extends UseQueryOptions<AxiosResponse<IDsmesDashboardCounts>>,
		Partial<IDsmesParams> {
	isEnabled?: boolean
}
export function useDsMesDashboardCounts({
	programCode,
	startDate,
	endDate,
	sessionCode,
	client,
	isEnabled = true,
	partner,
	...rest
}: IDsmesDashboardCountQueryProps) {
	return useQuery<AxiosResponse<IDsmesDashboardCounts>, any, AxiosResponse<IDsmesDashboardCounts>>(
		[ENDPOINTS.DSMES_DASHBOARD_COUNT, programCode, endDate, partner, client, sessionCode],
		() => {
			const CancelToken = axios.CancelToken
			const source = CancelToken.source()

			return getDsmesDashboardCounts(
				{ programCode, startDate, client, partner, endDate, sessionCode },
				source,
			)
		},
		{
			keepPreviousData: true,
			refetchOnWindowFocus: false,
			enabled: isEnabled,
			...rest,
		},
	)
}

/**
 * DSMES PATIENT ENROLLED
 */

interface IDsmesPatientEnrolledQueryProps
	extends UseQueryOptions<AxiosResponse<IDsmesPatientEnrolled>>,
		Partial<IDsmesParams> {
	isEnabled?: boolean
}
export function useDsmesPatientEnrolled({
	programCode,
	startDate,
	endDate,
	sessionCode,
	partner,
	client,
	isEnabled = true,
	...rest
}: IDsmesPatientEnrolledQueryProps) {
	return useQuery<AxiosResponse<IDsmesPatientEnrolled>, any, AxiosResponse<IDsmesPatientEnrolled>>(
		[
			ENDPOINTS.DSMES_DASHBOARD_PATIENT_ENROLLED,
			endDate,
			programCode,
			partner,
			client,
			sessionCode,
		],
		() => {
			const CancelToken = axios.CancelToken
			const source = CancelToken.source()

			return getDsmesPatientEnrolled(
				{ programCode, startDate, endDate, client, partner, sessionCode },
				source,
			)
		},
		{
			keepPreviousData: true,
			refetchOnWindowFocus: false,
			enabled: isEnabled,
			...rest,
		},
	)
}

/**
 * BILLABLE SESSIONS
 */

interface IDsmesBillableSessionsQueryProps
	extends UseQueryOptions<AxiosResponse<IDsmesBillableSessions>>,
		Partial<IDsmesParams> {
	isEnabled?: boolean
}
export function useDsmesBillableSessions({
	programCode,
	startDate,
	endDate,
	sessionCode,
	client,
	partner,
	isEnabled = true,
	...rest
}: IDsmesBillableSessionsQueryProps) {
	return useQuery<
		AxiosResponse<IDsmesBillableSessions>,
		any,
		AxiosResponse<IDsmesBillableSessions>
	>(
		[
			ENDPOINTS.DSMES_DASHBOARD_BILLABLE_SESSIONS,
			programCode,
			partner,
			endDate,
			client,
			sessionCode,
		],
		() => {
			const CancelToken = axios.CancelToken
			const source = CancelToken.source()

			return getDsmesBillableSessions(
				{ programCode, startDate, client, partner, endDate, sessionCode },
				source,
			)
		},
		{
			keepPreviousData: true,
			refetchOnWindowFocus: false,
			enabled: isEnabled,
			...rest,
		},
	)
}

/**
 * TOTAL COMPLETED SESSIONS
 */

interface IDsmesTotalCompletedSessionsQueryProps
	extends UseQueryOptions<AxiosResponse<IDsmesPatientEnrolled>>,
		Partial<IDsmesParams> {
	isEnabled?: boolean
}
export function useDsmesTotalCompletedSessions({
	programCode,
	sessionCode,
	client,
	partner,
	isEnabled = true,
	startDate,
	endDate,
	...rest
}: IDsmesTotalCompletedSessionsQueryProps) {
	return useQuery<AxiosResponse<IDsmesPatientEnrolled>, any, AxiosResponse<IDsmesPatientEnrolled>>(
		[
			ENDPOINTS.DSMES_DASHBOARD_TOTAL_COMPLETED_SESSIONS,
			programCode,
			endDate,
			partner,
			client,
			sessionCode,
		],
		() => {
			const CancelToken = axios.CancelToken
			const source = CancelToken.source()

			return getDsmesTotalCompletedSessions(
				{ programCode, client, startDate, endDate, partner, sessionCode },
				source,
			)
		},
		{
			keepPreviousData: true,
			refetchOnWindowFocus: false,
			enabled: isEnabled,
			...rest,
		},
	)
}

/**
 * SESSIONS COMPLETED
 */

interface IDsmesRegisteredSessionsCompletedQueryProps
	extends UseQueryOptions<AxiosResponse<IDsmesSessionCompleted[]>>,
		Partial<IDsmesParams> {
	isEnabled?: boolean
}
export function useDsmesRegisteredSessionsCompleted({
	programCode,
	startDate,
	endDate,
	sessionCode,
	client,
	partner,
	isEnabled = true,
	...rest
}: IDsmesRegisteredSessionsCompletedQueryProps) {
	return useQuery<
		AxiosResponse<IDsmesSessionCompleted[]>,
		any,
		AxiosResponse<IDsmesSessionCompleted[]>
	>(
		[
			ENDPOINTS.DSMES_DASHBOARD_REGISTERED_SESSION,
			partner,
			programCode,
			endDate,
			client,
			sessionCode,
		],
		() => {
			const CancelToken = axios.CancelToken
			const source = CancelToken.source()

			return getDsmesRegisteredSessionsCompleted(
				{ programCode, startDate, endDate, client, partner, sessionCode },
				source,
			)
		},
		{
			keepPreviousData: true,
			refetchOnWindowFocus: false,
			enabled: isEnabled,
			...rest,
		},
	)
}

/**
 * SESSIONS COMPLETED
 */

interface IDsmesRetentionRateQueryProps
	extends UseQueryOptions<AxiosResponse<IDsmesPatientEnrolled>>,
		Partial<IDsmesParams> {
	isEnabled?: boolean
}
export function useDsmesRetentionRate({
	programCode,
	startDate,
	endDate,
	sessionCode,
	partner,
	client,
	isEnabled = true,
	...rest
}: IDsmesRetentionRateQueryProps) {
	return useQuery<AxiosResponse<IDsmesPatientEnrolled>, any, AxiosResponse<IDsmesPatientEnrolled>>(
		[ENDPOINTS.DSMES_RETENTION_RATE, programCode, partner, endDate, client, sessionCode],
		() => {
			const CancelToken = axios.CancelToken
			const source = CancelToken.source()

			return getDsmesRetentionRate(
				{ programCode, partner, client, startDate, endDate, sessionCode },
				source,
			)
		},
		{
			keepPreviousData: true,
			refetchOnWindowFocus: false,
			enabled: isEnabled,
			...rest,
		},
	)
}

interface IDsmesConferenceDropdownQueryProps
	extends UseQueryOptions<IAxiosResponseWithPagination<IDsmesConference>>,
		Partial<IDsmesParams> {
	isEnabled?: boolean
	program: string
}
export function useDsmesSessions({
	isEnabled = true,
	search,
	page,
	program,
	...rest
}: IDsmesConferenceDropdownQueryProps) {
	return useQuery<
		IAxiosResponseWithPagination<IDsmesConference>,
		any,
		IAxiosResponseWithPagination<IDsmesConference>
	>(
		[ENDPOINTS.DSMES_SESSIONS, program, search, page],
		() => {
			const CancelToken = axios.CancelToken
			const source = CancelToken.source()
			return getDsmesSessions({ search, page }, program, source)
		},
		{
			keepPreviousData: true,
			refetchOnWindowFocus: false,
			enabled: isEnabled && !!program,
			...rest,
		},
	)
}

export const dsmesTilesIcon = ({
	membersEnrolled = 0,
	reportingGlucose = 0,
	glucoseReadings = 0,
	billableSession = 0,
}: {
	membersEnrolled: number
	reportingGlucose: number
	glucoseReadings: number
	billableSession: number
}) => {
	const stats = [
		{
			id: 1,
			tileName: 'Number Enrolled',
			icon: <ReportIconBlue className="w-12 h-12" />,
			value: membersEnrolled,
		},
		{
			id: 2,
			tileName: 'Reporting Glucose',
			icon: <ReportIconGreen className="w-12 h-12" />,
			value: reportingGlucose,
		},
		{
			id: 3,
			tileName: 'Glucose Readings',
			icon: <RightArrowDarkGreen className="w-12 h-12" />,
			value: glucoseReadings,
		},
		{
			id: 3,
			tileName: 'Billable Sessions',
			icon: <RightArrowDarkBlue className="w-12 h-12" />,
			value: billableSession,
		},
	]
	return stats
}

interface IDsmesClientDropdownQueryProps
	extends UseQueryOptions<IAxiosResponseWithPagination<IDsmesClients>>,
		Partial<IDsmesParams> {
	isEnabled?: boolean
}

export function useDsmesClients({
	limit,
	page,
	search,
	programCode,
	isEnabled,
}: IDsmesClientDropdownQueryProps) {
	return useQuery<
		IAxiosResponseWithPagination<IDsmesClients>,
		any,
		IAxiosResponseWithPagination<IDsmesClients>
	>(
		[ENDPOINTS.SERVICES_CLIENTS, limit, page, search, programCode],
		() => {
			const CancelToken = axios.CancelToken
			const source = CancelToken.source()
			return getDsmesClients(
				{
					limit,
					page,
					search,
					programCode,
				},
				source,
			)
		},
		{
			suspense: false,
			keepPreviousData: true,
			refetchOnWindowFocus: false,
			enabled: isEnabled,
		},
	)
}

interface IGetPartnerListingByClientQueryProps
	extends UseQueryOptions<IAxiosResponseWithPagination<Partial<IFullProviderDsmes>>>,
		Partial<IDsmesParams> {
	isEnabled?: boolean
	client?: string
	id?: string
}
export function useGetPartnerListProviderWise({
	limit,
	page,
	client,
	program,
	search,
	isEnabled = true,
	...rest
}: IGetPartnerListingByClientQueryProps = {}) {
	return useQuery<
		IAxiosResponseWithPagination<Partial<IFullProviderDsmes>>,
		any,
		IAxiosResponseWithPagination<Partial<IFullProviderDsmes>>
	>(
		[ENDPOINTS.PARTNER_LIST_BY_PROVIDER, program, limit, page, search],
		() => {
			const CancelToken = axios.CancelToken
			const source = CancelToken.source()
			return getPartnerListProviderWise({ client, program, limit, page, search }, source)
		},
		{
			suspense: false,
			keepPreviousData: true,
			refetchOnWindowFocus: false,
			...rest,
			enabled: isEnabled,
		},
	)
}

interface IProgramQueryProps
	extends UseQueryOptions<IAxiosResponseWithPagination<Partial<any>>>,
		Partial<IFullProviderDsmes> {
	isEnabled?: boolean
}
export function useGetProgramFilterForSuperAdmin({
	limit,
	page,
	clientId,
	search,
	isEnabled = true,
	...rest
}: IProgramQueryProps) {
	return useQuery<any>(
		[ENDPOINTS.GLOBAL_PROGRAMS_ALL, limit, page, clientId, search],
		() => {
			const CancelToken = axios.CancelToken
			const source = CancelToken.source()

			return getProgramListingForSuperAdmin(source, clientId, { limit, page, search })
		},
		{
			keepPreviousData: true,
			refetchOnWindowFocus: false,
			enabled: isEnabled,
			...rest,
		},
	)
}
